/**
 * Payment tab for a CRM contact — used inside the contact dialog.
 *
 * Sections:
 * 1. Payment methods — three ordered card slots (Primary / Second / Third)
 *    backed by vault credit cards. Requires the vault password to list cards;
 *    only the card nickname and last four digits are ever shown or stored.
 * 2. Payment rules — per-payment-type spending limits Zeus must obey. Above
 *    the limit, Zeus must get the user's approval (call → text → in-app).
 * 3. Pending approvals — approve or deny over-limit payment requests in-app.
 */

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CreditCard, Lock, Unlock, Trash2, ShieldCheck, Phone, Loader2, Plus, Check, X } from "lucide-react";

interface PaymentMethod {
  id: string;
  vaultRecordId: string;
  cardLabel: string;
  cardLast4: string;
  priority: number;
}
interface PaymentRule {
  id: string;
  paymentType: string;
  maxAmountCents: number;
}
interface PaymentApproval {
  id: string;
  paymentType: string;
  amountCents: number;
  description: string | null;
  status: string;
  requestedVia: string;
  createdAt: string;
}
interface PaymentsResponse {
  methods: PaymentMethod[];
  rules: PaymentRule[];
  approvals: PaymentApproval[];
  vaultUnlocked: boolean;
  approvalPhone: string | null;
}
interface VaultCard {
  vaultRecordId: string;
  name: string;
  last4: string;
}

const SLOTS = [
  { priority: 1, label: "Primary" },
  { priority: 2, label: "Second" },
  { priority: 3, label: "Third" },
];

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function ContactPaymentTab({ contactId }: { contactId: string }) {
  const { toast } = useToast();
  const [vaultPassword, setVaultPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [newRuleType, setNewRuleType] = useState("");
  const [newRuleLimit, setNewRuleLimit] = useState("");
  const [phoneDraft, setPhoneDraft] = useState<string | null>(null);

  const paymentsKey = [`/api/contacts/${contactId}/payments`];
  const cardsKey = [`/api/contacts/${contactId}/payments/cards`];

  const { data, isLoading } = useQuery<PaymentsResponse>({
    queryKey: paymentsKey,
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: cardsData } = useQuery<{ cards: VaultCard[] }>({
    queryKey: cardsKey,
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!data?.vaultUnlocked,
    retry: false,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: paymentsKey });
    queryClient.invalidateQueries({ queryKey: cardsKey });
  };

  const unlockVault = async () => {
    if (!vaultPassword) return;
    setUnlocking(true);
    try {
      await apiRequest("POST", "/api/vault/unlock", { password: vaultPassword });
      setVaultPassword("");
      refresh();
      toast({ title: "Vault unlocked", description: "Your cards are available for the next few minutes." });
    } catch (err: any) {
      toast({ title: "Could not unlock vault", description: err?.message ?? "Check your vault password.", variant: "destructive" });
    } finally {
      setUnlocking(false);
    }
  };

  const assignCard = useMutation({
    mutationFn: (vars: { vaultRecordId: string; priority: number }) =>
      apiRequest("POST", `/api/contacts/${contactId}/payment-methods`, vars),
    onSuccess: refresh,
    onError: (err: any) =>
      toast({ title: "Could not assign card", description: err?.message ?? "Try again.", variant: "destructive" }),
  });

  const removeMethod = useMutation({
    mutationFn: (methodId: string) =>
      apiRequest("DELETE", `/api/contacts/${contactId}/payment-methods/${methodId}`),
    onSuccess: refresh,
  });

  const addRule = useMutation({
    mutationFn: () => {
      const cents = Math.round(parseFloat(newRuleLimit) * 100);
      return apiRequest("POST", `/api/contacts/${contactId}/payment-rules`, {
        paymentType: newRuleType.trim(),
        maxAmountCents: cents,
      });
    },
    onSuccess: () => {
      setNewRuleType("");
      setNewRuleLimit("");
      refresh();
    },
    onError: (err: any) =>
      toast({ title: "Could not add rule", description: err?.message ?? "Try again.", variant: "destructive" }),
  });

  const deleteRule = useMutation({
    mutationFn: (ruleId: string) =>
      apiRequest("DELETE", `/api/contacts/${contactId}/payment-rules/${ruleId}`),
    onSuccess: refresh,
  });

  const resolveApproval = useMutation({
    mutationFn: (vars: { approvalId: string; decision: "approved" | "denied" }) =>
      apiRequest("POST", `/api/contacts/${contactId}/payment-approvals/${vars.approvalId}/resolve`, { decision: vars.decision }),
    onSuccess: refresh,
  });

  const savePhone = useMutation({
    mutationFn: (phone: string) => apiRequest("PATCH", "/api/payments/approval-phone", { phone }),
    onSuccess: () => {
      setPhoneDraft(null);
      refresh();
      toast({ title: "Approval phone saved" });
    },
    onError: (err: any) =>
      toast({ title: "Could not save phone", description: err?.message ?? "Use format +13105551234.", variant: "destructive" }),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading payment settings…
      </div>
    );
  }

  const cards = cardsData?.cards ?? [];
  const pendingApprovals = data.approvals.filter((a) => a.status === "pending");
  const ruleLimitValid = newRuleType.trim().length > 0 && parseFloat(newRuleLimit) >= 0 && Number.isFinite(parseFloat(newRuleLimit));

  return (
    <div className="space-y-8">
      {/* ── Payment methods ─────────────────────────────────────────────── */}
      <section>
        <h3 className="font-semibold flex items-center gap-2 mb-1">
          <CreditCard className="w-4 h-4" /> Payment methods
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Cards Zeus may use for this contact, in order: primary first, then second, then third. Only the card
          nickname and last four digits are stored here — full card details never leave your vault.
        </p>

        {!data.vaultUnlocked ? (
          <div className="border rounded-md p-4 space-y-3" data-testid="section-vault-locked">
            <p className="text-sm flex items-center gap-2">
              <Lock className="w-4 h-4" /> Enter your vault password to look up your credit cards.
            </p>
            <div className="flex gap-2 max-w-md">
              <Input
                type="password"
                placeholder="Vault password"
                value={vaultPassword}
                onChange={(e) => setVaultPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && unlockVault()}
                data-testid="input-vault-password"
              />
              <Button onClick={unlockVault} disabled={!vaultPassword || unlocking} data-testid="button-unlock-vault">
                {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4 mr-1" />} Unlock
              </Button>
            </div>
            {data.methods.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Current slots are shown below; unlock to change them.
              </p>
            )}
          </div>
        ) : cards.length === 0 ? (
          <p className="text-sm text-muted-foreground border rounded-md p-4">
            No credit cards found in your vault. Add a card in the Vault page first (category "Credit card").
          </p>
        ) : null}

        <div className="grid gap-3 mt-3 md:grid-cols-3">
          {SLOTS.map((slot) => {
            const method = data.methods.find((m) => m.priority === slot.priority);
            return (
              <div key={slot.priority} className="border rounded-md p-3 space-y-2" data-testid={`slot-payment-${slot.priority}`}>
                <div className="flex items-center justify-between">
                  <Badge variant={slot.priority === 1 ? "default" : "secondary"}>{slot.label}</Badge>
                  {method && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMethod.mutate(method.id)}
                      data-testid={`button-remove-method-${slot.priority}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {method ? (
                  <p className="text-sm font-medium" data-testid={`text-method-${slot.priority}`}>
                    {method.cardLabel} <span className="text-muted-foreground">•••• {method.cardLast4}</span>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Empty</p>
                )}
                {data.vaultUnlocked && cards.length > 0 && (
                  <Select
                    value=""
                    onValueChange={(v) => assignCard.mutate({ vaultRecordId: v, priority: slot.priority })}
                  >
                    <SelectTrigger data-testid={`select-card-${slot.priority}`}>
                      <SelectValue placeholder={method ? "Replace card…" : "Choose card…"} />
                    </SelectTrigger>
                    <SelectContent>
                      {cards.map((c) => (
                        <SelectItem key={c.vaultRecordId} value={c.vaultRecordId}>
                          {c.name} •••• {c.last4}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Rules ───────────────────────────────────────────────────────── */}
      <section>
        <h3 className="font-semibold flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4" /> Rules
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          Spending limits for this contact. Zeus can pay up to the limit without asking; anything above it (or any
          payment type without a rule) requires your approval — Zeus calls you first, texts if you don't answer, and
          you can always approve or deny right here.
        </p>

        <div className="space-y-2">
          {data.rules.map((rule) => (
            <div key={rule.id} className="flex items-center justify-between border rounded-md px-3 py-2" data-testid={`rule-${rule.id}`}>
              <div>
                <p className="font-medium text-sm">{rule.paymentType}</p>
                <p className="text-xs text-muted-foreground">Up to {dollars(rule.maxAmountCents)} without approval</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => deleteRule.mutate(rule.id)} data-testid={`button-delete-rule-${rule.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {data.rules.length === 0 && (
            <p className="text-sm text-muted-foreground border rounded-md p-3">
              No rules yet — every payment for this contact will require your approval.
            </p>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-2 mt-3">
          <Input
            placeholder="Payment type, e.g. Doctor bill"
            value={newRuleType}
            onChange={(e) => setNewRuleType(e.target.value)}
            data-testid="input-rule-type"
          />
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="Max amount ($)"
            value={newRuleLimit}
            onChange={(e) => setNewRuleLimit(e.target.value)}
            className="md:w-48"
            data-testid="input-rule-limit"
          />
          <Button
            onClick={() => addRule.mutate()}
            disabled={!ruleLimitValid || addRule.isPending}
            data-testid="button-add-rule"
          >
            <Plus className="w-4 h-4 mr-1" /> Add rule
          </Button>
        </div>
      </section>

      {/* ── Approval phone ──────────────────────────────────────────────── */}
      <section>
        <h3 className="font-semibold flex items-center gap-2 mb-1">
          <Phone className="w-4 h-4" /> Approval phone
        </h3>
        <p className="text-sm text-muted-foreground mb-3">
          The number Zeus calls (and texts, if you don't answer) when a payment needs your approval.
        </p>
        <div className="flex gap-2 max-w-md">
          <Input
            placeholder="+13105551234"
            value={phoneDraft ?? data.approvalPhone ?? ""}
            onChange={(e) => setPhoneDraft(e.target.value)}
            data-testid="input-approval-phone"
          />
          <Button
            variant="outline"
            disabled={phoneDraft === null || savePhone.isPending}
            onClick={() => savePhone.mutate((phoneDraft ?? "").trim())}
            data-testid="button-save-approval-phone"
          >
            Save
          </Button>
        </div>
      </section>

      {/* ── Pending approvals ───────────────────────────────────────────── */}
      {pendingApprovals.length > 0 && (
        <section>
          <h3 className="font-semibold mb-1">Pending approvals</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Zeus is waiting on your answer for these payments.
          </p>
          <div className="space-y-2">
            {pendingApprovals.map((a) => (
              <div key={a.id} className="flex items-center justify-between border rounded-md px-3 py-2" data-testid={`approval-${a.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm">
                    {dollars(a.amountCents)} — {a.paymentType}
                  </p>
                  {a.description && <p className="text-xs text-muted-foreground truncate">{a.description}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => resolveApproval.mutate({ approvalId: a.id, decision: "approved" })}
                    disabled={resolveApproval.isPending}
                    data-testid={`button-approve-${a.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => resolveApproval.mutate({ approvalId: a.id, decision: "denied" })}
                    disabled={resolveApproval.isPending}
                    data-testid={`button-deny-${a.id}`}
                  >
                    <X className="w-4 h-4 mr-1" /> Deny
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
